import {
  readEmbedParentContext,
  sanitizeEmbedAllowedOrigins,
  type EmbedWindowLike,
} from './embed-allowlist';

/** Class on `<html>` while the funnel is reporting its height to a host page. */
export const PORTAL_EMBED_ROOT_CLASS = 'nh-portal-embed';

/** CMS asks for the current height. The suffix is the iframe id inside that page. */
export const PORTAL_EMBED_HEIGHT_REQUEST = 'height-0';

export const PORTAL_EMBED_HEIGHT_MESSAGE_TYPE = 'size2portal';

/** Funnel shell or the blocked-embed error. The loading placeholder is intentionally excluded. */
const EMBED_CONTENT_SELECTOR = '.public-finder-shell, .public-finder-error';

const APP_ROUTE_PREFIXES = [
  '/login',
  '/register',
  '/accept-invite',
  '/verify-email',
  '/reset-password',
  '/admin',
  '/dashboard',
  '/finder',
];

export interface PortalEmbedHeightMessage {
  type: typeof PORTAL_EMBED_HEIGHT_MESSAGE_TYPE;
  params: { height: number };
}

export interface PortalEmbedDocument {
  documentElement: HTMLElement;
  body: HTMLElement | null;
  readyState: DocumentReadyState;
  querySelector: (selectors: string) => Element | null;
  querySelectorAll: (selectors: string) => Iterable<Element>;
  fonts?: { ready: Promise<unknown> };
}

export interface PortalEmbedParent {
  postMessage: (message: unknown, targetOrigin: string) => void;
}

export interface PortalEmbedHeightPort {
  document: PortalEmbedDocument;
  parent: PortalEmbedParent;
  self: unknown;
  top: unknown | null;
  location: EmbedWindowLike['location'];
  referrer: string;
  addEventListener: (type: string, listener: (event: MessageEvent) => void) => void;
  removeEventListener: (type: string, listener: (event: MessageEvent) => void) => void;
  addLoadListener: (listener: () => void) => void;
  removeLoadListener: (listener: () => void) => void;
  requestAnimationFrame: (callback: () => void) => number;
  ResizeObserver?: typeof ResizeObserver;
  MutationObserver?: typeof MutationObserver;
}

export interface PortalEmbedHeightBridge {
  setAllowedOrigins: (origins: readonly string[]) => void;
  stop: () => void;
}

/**
 * Public finder routes report height. The builder, dashboard and auth screens do not,
 * so an iframe that later navigates there does not keep resizing the host page.
 */
export function shouldReportPortalEmbedHeight(pathname: string): boolean {
  const path = pathnameFromUrl(pathname);
  return !APP_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function pathnameFromUrl(url: string): string {
  const withoutHash = url.split('#')[0] ?? '';
  const withoutQuery = withoutHash.split('?')[0] ?? '';
  if (!withoutQuery || withoutQuery === '/') {
    return '/';
  }
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}

export function isPortalHeightRequest(data: unknown): boolean {
  return data === PORTAL_EMBED_HEIGHT_REQUEST;
}

export function buildPortalHeightMessage(height: number): PortalEmbedHeightMessage {
  return {
    type: PORTAL_EMBED_HEIGHT_MESSAGE_TYPE,
    params: { height },
  };
}

/**
 * Height of the funnel itself. Returns 0 until the shell (or the embed error) is on screen,
 * so the loading spinner never schedules a shrink on the host page.
 */
export function measureEmbeddableContentHeight(doc: PortalEmbedDocument): number {
  const shell = doc.querySelector(EMBED_CONTENT_SELECTOR);
  if (!(shell instanceof HTMLElement)) {
    return 0;
  }
  const box = shell.getBoundingClientRect();
  return Math.ceil(Math.max(0, box.height, shell.scrollHeight, shell.offsetHeight));
}

/**
 * Post only to the page that actually embeds us.
 * An empty allowlist means "not configured yet" and still targets that parent.
 * A configured allowlist must include the parent. A single configured origin is
 * the fallback when the browser does not reveal the parent (no ancestorOrigins, no referrer).
 * `*` is never used.
 */
export function resolvePortalHeightTargetOrigin(options: {
  parentOrigin: string | null;
  allowedOrigins: readonly string[];
}): string | null {
  const allowed = sanitizeEmbedAllowedOrigins(options.allowedOrigins);
  const parent = normalizeHttpOrigin(options.parentOrigin);
  if (parent) {
    if (allowed.length === 0 || allowed.includes(parent)) {
      return parent;
    }
    return null;
  }
  return allowed.length === 1 ? allowed[0] : null;
}

export function createPortalEmbedHeightBridge(
  port: PortalEmbedHeightPort,
  measure: () => number = () => measureEmbeddableContentHeight(port.document),
): PortalEmbedHeightBridge {
  const root = port.document.documentElement;
  root.classList.add(PORTAL_EMBED_ROOT_CLASS);

  let stopped = false;
  let allowedOrigins: string[] = [];
  let parentOrigin = readInitialParentOrigin(port);
  let lastHeight: number | null = null;
  let publishForce = false;
  let publishScheduled = false;
  const observed = new Set<Element>();

  const resizeObserver = port.ResizeObserver
    ? new port.ResizeObserver(() => schedulePublish(false))
    : null;
  const mutationObserver = port.MutationObserver
    ? new port.MutationObserver(() => {
        watchContent();
        schedulePublish(false);
      })
    : null;

  const onMessage = (event: MessageEvent): void => {
    if (stopped || event.source !== port.parent) {
      return;
    }
    if (!isPortalHeightRequest(event.data)) {
      return;
    }
    const origin = normalizeHttpOrigin(event.origin);
    if (!origin) {
      return;
    }
    parentOrigin = origin;
    schedulePublish(true);
  };

  const onLoad = (): void => schedulePublish(true);

  const onFontsReady = (): void => {
    if (!stopped) {
      schedulePublish(false);
    }
  };

  port.addEventListener('message', onMessage);
  if (port.document.readyState !== 'complete') {
    port.addLoadListener(onLoad);
  }
  void port.document.fonts?.ready.then(onFontsReady);

  watchContent();
  resizeObserver?.observe(root);
  if (port.document.body) {
    resizeObserver?.observe(port.document.body);
  }
  mutationObserver?.observe(root, { childList: true, subtree: true });
  schedulePublish(true);

  return {
    setAllowedOrigins(origins: readonly string[]): void {
      allowedOrigins = sanitizeEmbedAllowedOrigins(origins);
      schedulePublish(true);
    },
    stop(): void {
      if (stopped) {
        return;
      }
      stopped = true;
      port.removeEventListener('message', onMessage);
      port.removeLoadListener(onLoad);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      observed.clear();
      root.classList.remove(PORTAL_EMBED_ROOT_CLASS);
    },
  };

  function watchContent(): void {
    if (!resizeObserver) {
      return;
    }
    for (const node of port.document.querySelectorAll(EMBED_CONTENT_SELECTOR)) {
      if (!(node instanceof Element) || observed.has(node)) {
        continue;
      }
      observed.add(node);
      resizeObserver.observe(node);
    }
  }

  function schedulePublish(force: boolean): void {
    publishForce = publishForce || force;
    if (publishScheduled || stopped) {
      return;
    }
    publishScheduled = true;
    port.requestAnimationFrame(() => {
      publishScheduled = false;
      const forceNow = publishForce;
      publishForce = false;
      if (stopped) {
        return;
      }
      publish(forceNow);
    });
  }

  function publish(force: boolean): void {
    const targetOrigin = resolvePortalHeightTargetOrigin({ parentOrigin, allowedOrigins });
    if (!targetOrigin) {
      return;
    }
    const height = measure();
    if (!Number.isFinite(height) || height <= 0) {
      return;
    }
    if (!force && height === lastHeight) {
      return;
    }
    lastHeight = height;
    port.parent.postMessage(buildPortalHeightMessage(height), targetOrigin);
  }
}

let activeBridge: PortalEmbedHeightBridge | null = null;
let activeWindow: Window | null = null;
let pendingAllowedOrigins: string[] | null = null;

export function startPortalEmbedHeightReporting(win: Window = window): void {
  if (!isHostedEmbed(win) || !shouldReportPortalEmbedHeight(win.location.pathname)) {
    return;
  }
  if (activeBridge && activeWindow === win) {
    return;
  }
  stopPortalEmbedHeightReporting();
  activeWindow = win;
  activeBridge = createPortalEmbedHeightBridge(portFromWindow(win));
  if (pendingAllowedOrigins) {
    activeBridge.setAllowedOrigins(pendingAllowedOrigins);
  }
}

export function stopPortalEmbedHeightReporting(): void {
  activeBridge?.stop();
  activeBridge = null;
  activeWindow = null;
}

export function syncPortalEmbedHeightReporting(url: string, win: Window = window): void {
  if (!isHostedEmbed(win) || !shouldReportPortalEmbedHeight(pathnameFromUrl(url))) {
    stopPortalEmbedHeightReporting();
    return;
  }
  startPortalEmbedHeightReporting(win);
}

/** Karma runs the suite inside an iframe. That frame is not a bank page. */
function isHostedEmbed(win: Window): boolean {
  if (win.self === win.top) {
    return false;
  }
  return !('__karma__' in win);
}

/** Restricts height reports to these origins once the finder config is loaded. */
export function setPortalEmbedHeightAllowedOrigins(origins: readonly string[]): void {
  const normalized = sanitizeEmbedAllowedOrigins(origins);
  pendingAllowedOrigins = normalized;
  activeBridge?.setAllowedOrigins(normalized);
}

function readInitialParentOrigin(port: PortalEmbedHeightPort): string | null {
  const ctx = readEmbedParentContext(
    {
      self: port.self,
      top: port.top,
      location: port.location,
      document: { referrer: port.referrer },
    },
    port.location.origin,
  );
  return ctx.parentOrigins[0] ?? null;
}

function normalizeHttpOrigin(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
}

function portFromWindow(win: Window): PortalEmbedHeightPort {
  return {
    document: win.document,
    parent: win.parent,
    self: win.self,
    top: win.top,
    location: win.location,
    referrer: win.document.referrer,
    addEventListener: (type, listener) => win.addEventListener(type, listener as EventListener),
    removeEventListener: (type, listener) =>
      win.removeEventListener(type, listener as EventListener),
    addLoadListener: (listener) => win.addEventListener('load', listener),
    removeLoadListener: (listener) => win.removeEventListener('load', listener),
    requestAnimationFrame: (callback) => win.requestAnimationFrame(callback),
    ResizeObserver,
    MutationObserver,
  };
}
